import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTrainingResults } from "@/hooks/putting/useTrainingResults";
import PuttingHistoryTabs from "@/components/putting/training/PuttingHistoryTabs";
import MultiTestResultsChart from "@/components/putting/training/MultiTestResultsChart";
import SwingDistanceCharts from "@/components/putting/training/charts/SwingDistanceCharts";
import GreenAttackZonesChart from "@/components/putting/training/charts/GreenAttackZonesChart";
import { filterByTestType } from "@/utils/puttingResults/formatUtils";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { Button } from "@/components/ui/button";

const SwingTraining = () => {
  const { t } = useTranslation('common');
  
  // State pour suivre le niveau sélectionné
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  
  // Utilisation du hook spécifique pour les entraînements
  const { 
    trainingResults, 
    isLoading, 
    chartDataByTest,
    testTypes,
    level1Results, 
    level2Results, 
    level3Results 
  } = useTrainingResults();

  // Debug logs
  useEffect(() => {
    if (trainingResults.length > 0) {
      console.log("All training results:", trainingResults);
      console.log("Test types available:", testTypes);
      console.log("SwingTraining - Training results count:", trainingResults.length);
    }
  }, [trainingResults, testTypes]);

  // Utiliser la fonction de filtrage améliorée et exclure les exercices de driver
  const swingResults = filterByTestType(trainingResults, 'swing').filter(result => {
    const testId = (result.test_id || '').toLowerCase();
    const testName = (result.test_name || '').toLowerCase();
    return !testId.includes('driver') && !testName.includes('driver');
  });
  
  const swingLevel1Results = filterByTestType(level1Results, 'swing').filter(result => {
    const testId = (result.test_id || '').toLowerCase();
    const testName = (result.test_name || '').toLowerCase();
    return !testId.includes('driver') && !testName.includes('driver');
  });
  
  const swingLevel2Results = filterByTestType(level2Results, 'swing').filter(result => {
    const testId = (result.test_id || '').toLowerCase();
    const testName = (result.test_name || '').toLowerCase();
    return !testId.includes('driver') && !testName.includes('driver');
  });
  
  const swingLevel3Results = filterByTestType(level3Results, 'swing').filter(result => {
    const testId = (result.test_id || '').toLowerCase();
    const testName = (result.test_name || '').toLowerCase();
    return !testId.includes('driver') && !testName.includes('driver');
  });

  console.log("Résultats de swing filtrés (sans driver):", swingResults.length);
  
  // Filtrer uniquement les résultats liés aux fers et bois de parcours (pas de driver)
  const swingTestTypes = testTypes.filter(type => {
    const typeLower = type.toLowerCase();
    return (typeLower.includes('fers') || 
           typeLower.includes('boisdeparcours') ||
           typeLower.includes('iron') ||
           typeLower.includes('fairwaywood')) && 
           !typeLower.includes('driver');
  });
  
  console.log("Types de tests de swing (sans driver):", swingTestTypes);

  // Gérer le changement de niveau dans les tabs
  const handleLevelChange = (level: string | null) => {
    setSelectedLevel(level);
  };

  // Filtrer les résultats selon le niveau sélectionné
  const getFilteredResults = () => {
    if (selectedLevel === "1") return swingLevel1Results;
    if (selectedLevel === "2") return swingLevel2Results;
    if (selectedLevel === "3") return swingLevel3Results;
    return swingResults;
  };

  // Préparer les données filtrées par type de test pour le graphique
  const filteredChartDataByTest: Record<string, any> = {};
  swingTestTypes.forEach(testType => {
    if (chartDataByTest[testType]) {
      filteredChartDataByTest[testType] = chartDataByTest[testType];
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="absolute top-20 left-6">
        <Button 
          variant="ghost" 
          className="text-golf-600 hover:text-golf-700 flex items-center gap-2" 
          asChild
        >
          <Link to="/training">
            <ChevronLeft className="h-5 w-5" />
            {t('swingTraining.backToTraining')}
          </Link>
        </Button>
      </div>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('swingTraining.title')}</h1>
          <p className="text-xl text-golf-600">{t('swingTraining.subtitle')}</p>
        </motion.div>
        
        <div className="my-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Results Charts - Now in the left wider column */}
          <div className="lg:col-span-2 space-y-6">
            <MultiTestResultsChart 
              chartDataByTest={filteredChartDataByTest}
              testTypes={swingTestTypes}
              selectedLevel={selectedLevel}
              trainingResults={swingResults}
            />
            
            <SwingDistanceCharts 
              selectedLevel={selectedLevel}
            />
            
            <GreenAttackZonesChart 
              selectedLevel={selectedLevel}
            />
          </div>
          
          {/* Results History - Now in the right narrower column */}
          <div className="lg:col-span-1">
            <PuttingHistoryTabs 
              puttingResults={swingResults}
              level1Results={swingLevel1Results}
              level2Results={swingLevel2Results}
              level3Results={swingLevel3Results}
              onLevelChange={handleLevelChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwingTraining;
